import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { z } from 'zod';

const emailSchema = z.string().email();

export function NewsletterSection() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = emailSchema.safeParse(email);
    if (!result.success) {
      toast.error(t('landing.newsletter.invalidEmail', 'Please enter a valid email'));
      return;
    }
    setStatus('loading');
    try {
      const { error } = await supabase
        .from('newsletter_subscribers' as any)
        .insert({ email: email.toLowerCase().trim() } as any);
      if (error && error.code === '23505') {
        // Already subscribed
        setStatus('success');
        return;
      }
      if (error) throw error;
      setStatus('success');
      if (window.dataLayer) {
        window.dataLayer.push({ event: 'newsletter_signup' });
      }
    } catch {
      toast.error(t('landing.newsletter.error', 'Something went wrong. Please try again.'));
      setStatus('idle');
    }
  };

  if (status === 'success') {
    return (
      <section className="py-16" style={{ background: 'linear-gradient(135deg, #1A1F3C 0%, #2A3060 100%)' }}>
        <div className="container">
          <div className="mx-auto max-w-xl text-center">
            <CheckCircle2 className="h-12 w-12 text-[#00C896] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">
              {t('landing.newsletter.successTitle', 'You\'re in!')}
            </h3>
            <p className="text-white/70">
              {t('landing.newsletter.successMessage', 'You\'ll receive our insights soon.')}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16" style={{ background: 'linear-gradient(135deg, #1A1F3C 0%, #2A3060 100%)' }}>
      <div className="container">
        <div className="mx-auto max-w-xl text-center">
          <Mail className="h-10 w-10 text-[#4F6CFF] mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">
            {t('landing.newsletter.title', 'Get RevOps insights every week')}
          </h2>
          <p className="text-white/60 mb-6">
            {t('landing.newsletter.subtitle', 'Practical Revenue Operations tips, market benchmarks and strategies for predictable revenue. Free, straight to your inbox.')}
          </p>
          <form onSubmit={handleSubmit} className="flex gap-3 max-w-md mx-auto">
            <Input
              type="email"
              placeholder={t('landing.newsletter.placeholder', 'Your best email')}
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-[#4F6CFF]"
              required
            />
            <Button type="submit" disabled={status === 'loading'} className="bg-[#4F6CFF] hover:bg-[#3D5AE8] text-white whitespace-nowrap">
              {status === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : t('landing.newsletter.cta', 'Subscribe')}
            </Button>
          </form>
          <p className="text-white/40 text-xs mt-3">
            {t('landing.newsletter.noSpam', 'No spam. Unsubscribe anytime.')}
          </p>
        </div>
      </div>
    </section>
  );
}
